import { action, computed, observable } from "mobx";
import { message } from "antd";
import { Declarant, DeclarantId } from "../models/Declarant";
import { Pagination } from "../models/Pagination";
import ee from "../services/EventEmitter";
import {
  CHANGE_TABLE_CELL_EVENT,
  DELETED_ENTITY,
  UPDATE_DECLARANT_EVENT,
  UPLOAD_XML_EVENT,
} from "../constants/events";
import {
  BRUTTO_COLUMN,
  CARGO_QUANTITY,
  CONSIGNMENT_ITEMS_COLUMN,
  INVOICE_COLUMN,
  WARRANTY,
} from "../constants/table";
import { Summary } from "../components/DeclarantGrid/Interfaces";
import {
  IDeclarantSummary,
  ImportDeclarant,
} from "../components/declarants/Interfaces";
import { defaultDateRange } from "../components/declarants/dateRange";
import {
  IObjectMap,
  LoadDeclarants,
  UpdateDeclarantKeys,
  UpdateDeclarantValue,
} from "../interfaces/common";
import ApiManager from "../mixins/ApiManager";
import {
  EIGHT_NUMBER,
  EIGHTS_KEY,
  OTHERS_KEY,
  TREE_NUMBER,
  TRIPLETS_KEY,
} from "../constants/measureCodes";
import fillGeneralSummaryData from "../utils/summary";
import { RefreshInterface } from "./interfaces/RefreshInterface";
import { DeepPartial, Nullable } from "../models/Common";
import {
  DeclarantTwsSyncErrorResponse,
  DeclarantTwsSyncRequest,
  DeclarantTwsSyncResponseAny,
  DeclarantTwsSyncSuccessResponse,
} from "../models/DeclarantTwsSync";
import { DeclarantAssignClientResponse } from "../models/DeclarantAssignClient";

const defaultPagination = {
  total: 0,
  per_page: 0,
  current_page: 0,
  last_page: 999,
  from: 0,
  to: 0,
};

const defaultParameters = {
  ...defaultDateRange,
  page: 0,
};

abstract class AbstractDeclarantStore
  extends ApiManager
  implements
    ImportDeclarant,
    IDeclarantSummary,
    LoadDeclarants,
    RefreshInterface {
  @observable
  selectedRows: (string | number)[] = [];

  @observable
  protected _data: Declarant[] = [];

  @observable
  isLoadingTableData = false;

  @observable
  isAssigningClient = false;

  @observable
  lastTwsUpdateDate: Date | string = "";

  @observable
  summary: Summary[] = [
    {
      key: BRUTTO_COLUMN,
      selected: 0,
      general: 0,
    },
    {
      key: CONSIGNMENT_ITEMS_COLUMN,
      selected: 0,
      general: 0,
    },
    {
      key: INVOICE_COLUMN,
      selected: 0,
      general: 0,
    },
    {
      key: CARGO_QUANTITY,
      selected: 0,
      general: 0,
    },
    {
      key: WARRANTY,
      selected: 0,
      general: 0,
    },
  ];

  @observable
  measureCodes: Summary[] = [
    {
      key: TRIPLETS_KEY,
      selected: 0,
      general: 0,
      updateSelected(data: Declarant[]): void {
        this.selected = data.filter(
          (item) => item.measure_code === TREE_NUMBER
        ).length;
      },
      updateAfterRemove(entity: Declarant): void {
        if (entity.measure_code === TREE_NUMBER) {
          this.general -= 1;
        }
      },
    },
    {
      key: EIGHTS_KEY,
      selected: 0,
      general: 0,
      updateSelected(data: Declarant[]): void {
        this.selected = data.filter(
          (item) => item.measure_code === EIGHT_NUMBER
        ).length;
      },
      updateAfterRemove(entity: Declarant): void {
        if (entity.measure_code === EIGHT_NUMBER) {
          this.general -= 1;
        }
      },
    },
    {
      key: OTHERS_KEY,
      selected: 0,
      general: 0,
      updateSelected(data: Declarant[]): void {
        const condition = [TREE_NUMBER, EIGHT_NUMBER];
        this.selected = data.filter(
          (item) => !condition.includes(item.measure_code)
        ).length;
      },
      updateAfterRemove(entity: Declarant): void {
        const condition = [TREE_NUMBER, EIGHT_NUMBER];
        if (!condition.includes(entity.measure_code)) {
          this.general -= 1;
        }
      },
    },
  ];

  protected _pagination: Pagination = defaultPagination;

  @observable
  hasMoreItems = false;

  @observable
  protected _parameters: IObjectMap = defaultParameters;

  @computed
  get parameters(): IObjectMap {
    return this._parameters;
  }

  @observable
  protected _isUploadingXmlFile = false;

  get isUploadingXmlFile(): boolean {
    return this._isUploadingXmlFile;
  }

  get data(): Declarant[] {
    return this._data;
  }

  get pagination(): Pagination {
    return this._pagination;
  }

  constructor() {
    super();

    ee.on(CHANGE_TABLE_CELL_EVENT, (value: UpdateDeclarantValue) => {
      this.updateEntity(value.entityId, value.entityKey, value.value);
    });

    ee.on(UPDATE_DECLARANT_EVENT, (value: DeepPartial<Declarant>) => {
      if (value.id) {
        console.log("Passed here");
        this.updateEntity(value.id, undefined, undefined, { ...value });
      }
    });

    ee.on(UPLOAD_XML_EVENT, () => {
      this.load({}, true);
    });

    this.init();
  }

  init(): void {
    // implement if needed
  }

  load(parameters: IObjectMap | null, needUpdateData = false): void {
    if (parameters) {
      this._parameters = {
        ...this._parameters,
        ...parameters,
        page: needUpdateData ? 1 : this._pagination.current_page + 1,
      };
    }

    this.loadTableData(needUpdateData);
  }

  private async resolveErrorString(e: unknown): Promise<string> {
    let resolvedErrorString: string = "Unknown error";
    if (e instanceof Response) {
      const error = await e.json();
      if (error.message) {
        resolvedErrorString = error.message;
      }
    } else {
      resolvedErrorString =
        typeof e === "object" && e && "message" in e
          ? JSON.stringify((e as { message: unknown })["message"])
          : `${JSON.stringify(e)}`;
    }
    return resolvedErrorString;
  }

  @action
  private loadTableData(refresh = false): void {
    if (!this.hasMoreItems && !refresh) {
      return;
    }

    this.isLoadingTableData = true;

    this.hasMoreItems = false;

    this.getDeclarants()
      .then(async (response) => {
        const items = await response.json();

        this.lastTwsUpdateDate = new Date(items.tws_last_sync);

        this._data = refresh
          ? items.pagination.data
          : this._data.concat(items.pagination.data);

        this.calculateSummary(items.summary as { [key: string]: unknown });

        const page = items.pagination.current_page as number;
        const lastPage = items.pagination.last_page as number;

        this._pagination = {
          total: items.pagination.total,
          per_page: items.pagination.per_page,
          current_page: page,
          last_page: lastPage,
          from: items.pagination.from,
          to: items.pagination.to,
        };

        this.hasMoreItems = page < lastPage;
      })
      .catch(() => {
        this._data = [];
        this._pagination = {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          from: 0,
          to: 0,
        };
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  @action
  importDeclarant(fileList: FileList): void {
    this._isUploadingXmlFile = true;

    const promises = Array.from(fileList).map((file) => {
      return this.sendImportXmlRequest(file)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            message.success(`File ${file.name}. ${data.message}`);
          }
        })
        .catch(async (response) => {
          const data = await response.json();

          message.error(`File ${file.name}. ${data.message}`);
        });
    });

    Promise.all(promises).finally(() => {
      ee.emit(UPLOAD_XML_EVENT, true);

      this._isUploadingXmlFile = false;
    });
  }

  private sendImportXmlRequest(file: File) {
    const formData = new FormData();
    formData.append("file", file, file.name);

    return this.apiManager.sendImportFile(formData);
  }

  protected getDeclarants(): Promise<Response> {
    return this.apiManager.getDeclarants(this._parameters);
  }

  @action
  private updateEntity(
    id: unknown,
    key?: UpdateDeclarantKeys,
    value?: string | number | null,
    valuesObject?: object
  ): void {
    const index = this.data.findIndex((item) => {
      return item.id === id;
    });

    if (index < 0) {
      return;
    }

    this.isLoadingTableData = true;

    const values: Record<
      string,
      Nullable<string | number | object>
    > = valuesObject ? { ...valuesObject } : key ? { [key]: value } : {};

    this.apiManager
      .updateDeclarant(id, { ...values })
      .then(async (response) => {
        const data = await response.json();

        if (!response.ok) {
          message.error(data.message);
          return;
        }

        const newData = [...this.data] as Declarant[];

        if (data.item && newData[index]) {
          newData[index] = {
            ...newData[index],
            ...data.item,
          } as Declarant;
        }

        this._data = newData;

        message.success(data.message);
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  @action
  async twsSync(
    params: DeclarantTwsSyncRequest,
    existingOnly?: boolean
  ): Promise<DeclarantTwsSyncResponseAny> {
    try {
      let request: Response;
      if (existingOnly) {
        request = await this.apiManager.declarantExistingTwsSync({
          ...params,
        });
      } else {
        request = await this.apiManager.declarantTwsSync({
          ...params,
        });
      }
      const data:
        | DeclarantTwsSyncSuccessResponse
        | DeclarantTwsSyncErrorResponse = await request.json();
      if ("imported" in data && "skipped" in data) {
        message.success(
          `The data was synchronised successfully: ${data.imported} imported, ${data.skipped} skipped`
        );
        return {
          success: true,
          data: { ...data },
        };
      }

      console.warn("TWS sync error server response: ", data);
      throw new Error(`${data.message}`);
    } catch (e) {
      const errorString = await this.resolveErrorString(e);
      message.error(`The data was not synchronised: ${errorString}`);
      console.error("TWS sync error: ", e);
      return {
        success: false,
        data: {
          message: errorString,
        },
      };
    }
  }

  @action
  async singleTwsSync(id: DeclarantId): Promise<void> {
    try {
      await this.apiManager.singleDeclarantTwsSync(id);
      message.success(
        "The data for the declarant was synchronised successfully"
      );
    } catch (e) {
      const errorString = await this.resolveErrorString(e);
      message.error(
        `The data for the declarant was not synchronised: ${errorString}`
      );
      console.error(`${id} TWS sync error: `, e);
    }
  }

  @action
  async assignClientToSelected(clientId: number): Promise<void> {
    if (this.selectedRows.length === 0) {
      return;
    }

    this.isAssigningClient = true;

    try {
      const response = await this.apiManager.assignClientToDeclarants(
        this.selectedRows as DeclarantId[],
        clientId
      );
      const data: DeclarantAssignClientResponse = await response.json();

      message.success(`Client assigned to ${data.updated.length} declarant(s)`);

      if (data.skipped.length > 0) {
        message.warning(
          `${
            data.skipped.length
          } declarant(s) were left unchanged: ${data.skipped.join(", ")}`
        );
      }

      this.load({}, true);
    } catch (e) {
      const errorString = await this.resolveErrorString(e);
      message.error(`Failed to assign client: ${errorString}`);
      console.error("Assign client error: ", e);
    } finally {
      this.isAssigningClient = false;
    }
  }

  protected deleteEntity(id: unknown): void {
    this.apiManager.deleteDeclarant(id).then(async (response) => {
      const data = await response.json();

      if (response.ok) {
        const entity = this._data.find(
          (item) => item.id === id
        ) as Declarant | null;

        this._data = this._data.filter((item) => {
          return item.id !== id;
        });

        if (entity) {
          this.summary.forEach((item) => {
            item.general -=
              (entity[item.key as keyof Declarant] as number) || 0;
            item.selected -=
              (entity[item.key as keyof Declarant] as number) || 0;
          });

          this.measureCodes.forEach((item) => {
            if (!item.updateAfterRemove) {
              return;
            }

            item.updateAfterRemove(entity as Declarant);
          });

          const items = this.findItemsByIds(this.selectedRows);
          this.recalculateSelectedMeasureCodes(items);
        }

        message.success(data.message);

        ee.emit(DELETED_ENTITY, true);

        return;
      }

      message.error(data.message);
    });
  }

  calculateSummary(total: { [key: string]: unknown }): void {
    const summary = this.getEmptySummary();
    this.summary = fillGeneralSummaryData(summary, total);
    const codes = this.getEmptyMeasureCodes();
    this.measureCodes = fillGeneralSummaryData(codes, total);

    if (this.selectedRows.length > 0) {
      const items = this.findItemsByIds(this.selectedRows);
      this.recalculateSelectedSummary(items);
      this.recalculateSelectedMeasureCodes(items);
    }
  }

  private getEmptySummary(): Summary[] {
    return this.summary.map((item) => ({
      ...item,
      selected: 0,
    }));
  }

  private getEmptyMeasureCodes(): Summary[] {
    return this.measureCodes.map((item) => ({
      ...item,
      selected: 0,
    }));
  }

  private recalculateSelectedSummary(data: Declarant[]): void {
    const copy = this.getEmptySummary();

    data.forEach((declarant) => {
      copy.forEach((summary) => {
        // @ts-ignore
        const value = declarant[summary.key] || 0;

        summary.selected += parseFloat(value.toString());
      });
    });

    this.summary = copy;
  }

  private recalculateSelectedMeasureCodes(data: Declarant[]): void {
    const copy = this.getEmptyMeasureCodes();

    copy.forEach((code) => {
      if (code.updateSelected) {
        code.updateSelected(data);
      }
    });

    this.measureCodes = copy;
  }

  @action
  changeSelectedRows(ids: (string | number)[]): void {
    this.selectedRows = ids;

    const items = this.findItemsByIds(ids);

    this.recalculateSelectedSummary(items);
    this.recalculateSelectedMeasureCodes(items);
  }

  private findItemsByIds(ids: (string | number)[]) {
    return this.data.filter((item) => ids.includes(item.id as string | number));
  }

  @action
  refresh(): void {
    this.load(
      {
        ...this.parameters,
        page: 1,
      },
      true
    );
  }
}

export default AbstractDeclarantStore;
